import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Input } from './ui/input';
import { Loader2, Search, Image, Check } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const MediaPicker = ({ onSelect, onClose, selectedId = null }) => {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selected, setSelected] = useState(selectedId);
  
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  
  useEffect(() => {
    const fetchMedia = async () => {
      try {
        const response = await axios.get(`${API}/media`, { headers });
        // Filter to only show images (API uses mime_type field)
        const images = response.data.filter(item => 
          item.mime_type?.startsWith('image/') || 
          item.content_type?.startsWith('image/') || 
          item.filename?.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i)
        );
        setMedia(images);
      } catch (error) {
        console.error('Error fetching media:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchMedia();
  }, [token]);
  
  const filteredMedia = media.filter(item =>
    item.filename?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.alt_text?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.title?.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  const handleSelect = (item) => {
    setSelected(item.id);
    if (onSelect) {
      onSelect(item);
    }
  };
  
  const getImageUrl = (item) => {
    const url = item.url || `/api/media/file/${item.filename}`;
    // Make relative URLs absolute for img src
    if (url.startsWith('/')) return `${BACKEND_URL}${url}`;
    return url;
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-yellow-500" />
        <span className="ml-3 text-gray-400">Laster bilder...</span>
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Søk etter bilder..."
          className="pl-10 bg-gray-900 border-gray-600 text-white"
        />
      </div>
      
      {/* Grid */}
      {filteredMedia.length === 0 ? (
        <div className="text-center py-12">
          <Image className="w-12 h-12 text-gray-600 mx-auto mb-3" />
          <p className="text-gray-400">
            {searchQuery ? 'Ingen bilder funnet' : 'Ingen bilder i mediearkivet'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 max-h-[400px] overflow-y-auto">
          {filteredMedia.map((item) => (
            <button
              key={item.id}
              onClick={() => handleSelect(item)}
              className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all hover:opacity-90 ${
                selected === item.id 
                  ? 'border-yellow-500 ring-2 ring-yellow-500/50' 
                  : 'border-gray-700 hover:border-gray-500'
              }`}
            >
              <img
                src={getImageUrl(item)}
                alt={item.alt_text || item.filename}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23374151" width="100" height="100"/><text fill="%239CA3AF" x="50%" y="50%" text-anchor="middle" dy=".3em" font-size="12">Feil</text></svg>';
                }}
              />
              {selected === item.id && (
                <div className="absolute inset-0 bg-yellow-500/20 flex items-center justify-center">
                  <div className="w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center">
                    <Check className="w-5 h-5 text-black" />
                  </div>
                </div>
              )}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                <p className="text-white text-xs truncate">
                  {item.title || item.filename}
                </p>
              </div>
            </button>
          ))}
        </div>
      )}
      
      <p className="text-gray-500 text-sm">
        Klikk på et bilde for å velge det. {filteredMedia.length} bilder tilgjengelig.
      </p>
    </div>
  );
};

export default MediaPicker;
